import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';
import Layout from '../components/Layout';
import '../styles/custom-ai-solutions.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const CustomAISolutions = ({ user }) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    useCase: '',
    solutionType: 'predictive'
  });
  const [submitStatus, setSubmitStatus] = useState('');
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access custom AI solution services.</p>
        </div>
      </Layout>
    );
  }

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.useCase) {
      setSubmitStatus('Please provide your name, email address, and use case.');
      return;
    }
    
    setSubmitStatus('Submitting...');
    
    try {
      // Get AWS credentials from Amplify Auth
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      // Configure AWS SDK with proper credentials
      AWS.config.update({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });
      
      // Send email notification
      const ses = new AWS.SES();
      const emailParams = {
        Source: 'info@relufox.ai',
        Destination: {
          ToAddresses: ['info@relufox.ai']
        },
        Message: {
          Subject: {
            Data: 'New Custom AI Solution Inquiry - Fox River AI'
          },
          Body: {
            Text: {
              Data: `A new inquiry has been submitted for custom AI solutions:\n\nCONTACT INFORMATION:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company || 'Not provided'}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nSOLUTION TYPE: ${contactInfo.solutionType}\n\nUSE CASE:\n${contactInfo.useCase}\n\nPlease follow up with this customer.`
            }
          }
        }
      };
      
      await ses.sendEmail(emailParams).promise();
      
      setSubmitStatus('Thank you! Your inquiry has been submitted. We will contact you within 24 hours.');
      setContactInfo({
        name: '',
        email: '',
        company: '',
        phone: '',
        useCase: '',
        solutionType: 'predictive'
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Submission failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="custom-ai-solutions-page">
        <div className="hero-section">
          <h1>Custom AI Solutions</h1>
          <p className="hero-subtitle">Tailored AI systems that solve your specific business problems</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>Beyond Off-the-Shelf Solutions</h2>
            <p className="section-intro">At Fox River AI, we build tailored AI systems that solve your specific business problems — no off-the-shelf templates here. Whether you're looking to reduce customer churn, forecast demand, automate internal workflows, or analyze unstructured data, we work closely with your team to design, train, and deploy the right solution for your use case.</p>
            
            <div className="services-grid">
              <div className="service-item">
                <h3>Predictive Models</h3>
                <p>Custom forecasting and prediction systems for your specific business metrics</p>
              </div>
              <div className="service-item">
                <h3>Automation Systems</h3>
                <p>AI-powered workflows that reduce manual tasks and increase efficiency</p>
              </div>
              <div className="service-item">
                <h3>Data Analysis</h3>
                <p>Extract insights from your structured and unstructured data</p>
              </div>
              <div className="service-item">
                <h3>Private LLM Deployments</h3>
                <p>Secure, on-premises large language models trained on your data</p>
              </div>
            </div>
          </section>

          <section className="lifecycle-section">
            <h2>Full Lifecycle Support</h2>
            <div className="lifecycle-steps">
              <div className="lifecycle-step">
                <div className="step-number">1</div>
                <h3>Consultation</h3>
                <p>We work with you to understand your business needs and define success criteria</p>
              </div>
              <div className="lifecycle-step">
                <div className="step-number">2</div>
                <h3>Data Preparation</h3>
                <p>We clean, structure, and optimize your data for AI training</p>
              </div>
              <div className="lifecycle-step">
                <div className="step-number">3</div>
                <h3>Model Development</h3>
                <p>We build and train custom AI models specific to your use case</p>
              </div>
              <div className="lifecycle-step">
                <div className="step-number">4</div>
                <h3>Integration</h3>
                <p>We deploy your solution and integrate it with your existing systems</p>
              </div>
              <div className="lifecycle-step">
                <div className="step-number">5</div>
                <h3>Ongoing Support</h3>
                <p>We provide maintenance, updates, and continuous improvement</p>
              </div>
            </div>
          </section>

          <section className="benefits-section">
            <h2>Why Choose Custom AI?</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Tailored to Your Needs</h3>
                <p>Solutions designed specifically for your business challenges</p>
              </div>
              <div className="benefit-item">
                <h3>Data Privacy</h3>
                <p>Your data stays within your control, not shared with third parties</p>
              </div>
              <div className="benefit-item">
                <h3>Competitive Advantage</h3>
                <p>Unique AI capabilities that differentiate you from competitors</p>
              </div>
              <div className="benefit-item">
                <h3>Ownership</h3>
                <p>You own the models and intellectual property we develop for you</p>
              </div>
            </div>
          </section>

          <section className="inquiry-section">
            <h2>Start Your Custom AI Journey</h2>
            <p>Tell us about your business challenges, and we'll explore how a custom AI solution can help.</p>
            
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Full Name *" 
                  value={contactInfo.name}
                  onChange={(e) => handleContactInfoChange('name', e.target.value)}
                  className="form-input"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email Address *" 
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Company (Optional)" 
                  value={contactInfo.company}
                  onChange={(e) => handleContactInfoChange('company', e.target.value)}
                  className="form-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone (Optional)" 
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={contactInfo.solutionType}
                  onChange={(e) => handleContactInfoChange('solutionType', e.target.value)}
                  className="form-select"
                >
                  <option value="predictive">Predictive Models</option>
                  <option value="automation">Automation Systems</option>
                  <option value="analysis">Data Analysis</option>
                  <option value="llm">Private LLM Deployment</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <textarea
                placeholder="Describe your business challenge and what you'd like AI to help with *"
                value={contactInfo.useCase}
                onChange={(e) => handleContactInfoChange('useCase', e.target.value)}
                className="form-textarea"
                rows="5"
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Request Consultation
              </button>
              {submitStatus && <p className="submit-status">{submitStatus}</p>}
            </form>
            
            <div className="contact-alternative">
              <p>Prefer to discuss your needs directly?</p>
              <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
              <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default CustomAISolutions;